/**
 * Zentrale Theme-Registry.
 *
 * Ein Theme besteht aus einem Eintrag hier und einer CSS-Datei unter
 * src/styles/themes/<id>.css mit dem Selektor :root[data-theme='<id>'].
 * Siehe src/styles/themes/README.md.
 */

import { clearAppearance, paintAppearance } from './appearance.ts';
import { isDarkSide, mixColor, parseTone, shapeTone } from './mix.ts';

export interface ThemeDef {
  /** Wert fuer das data-theme-Attribut am <html>-Element. */
  id: string;
  name: string;
  description: string;
  /** Steuert, wie iOS Formularelemente und Scrollbalken faerbt. */
  colorScheme: 'light' | 'dark';
  /** Farbe der Statusleiste im Homescreen-Modus. */
  themeColor: string;
}

export const THEMES: ThemeDef[] = [
  {
    id: 'paper',
    name: 'Hell',
    description: 'Warmes, helles Grau mit feinen Linien und Überschriften in Serifenschrift.',
    colorScheme: 'light',
    themeColor: '#faf9f5',
  },
  {
    id: 'dark',
    name: 'Dunkel',
    description: 'Anthrazit mit warmem Unterton. Angenehm abends und auf der Bühne.',
    colorScheme: 'dark',
    themeColor: '#262624',
  },
  // Weitere Designs hier ergaenzen.
];

export const DEFAULT_THEME_ID = 'paper';

/** Sonderwert: richtet sich nach der Einstellung des Geraets. */
export const AUTO_THEME_ID = 'auto';

export function findTheme(id: string): ThemeDef {
  return THEMES.find((t) => t.id === id) ?? (THEMES[0] as ThemeDef);
}

/** Folgt das Geraet gerade dem dunklen Erscheinungsbild? */
function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Helligkeit 0 (hell) bis 1 (dunkel) fuer einen Theme-Wert.
 * "auto" folgt dem Geraet; Zwischenstufen stehen als "tone:0.42" da.
 */
export function themeTone(id: string): number {
  if (id === AUTO_THEME_ID) return prefersDark() ? 1 : 0;
  return parseTone(id) ?? (findTheme(id).colorScheme === 'dark' ? 1 : 0);
}

/**
 * Loest einen Theme-Wert in ein echtes Theme auf. Bei Zwischenstufen ist
 * das die naehere Seite; sie bestimmt Formularelemente und Schatten.
 */
export function resolveTheme(id: string): ThemeDef {
  if (id === AUTO_THEME_ID || parseTone(id) !== null) return findTheme(isDarkSide(themeTone(id)) ? 'dark' : 'paper');
  return findTheme(id);
}

/**
 * Flaechen und Linien: werden zwischen Hell und Dunkel gemischt.
 * Schrift, Akzente und alles Uebrige (Schatten, Schriften) kommen von der
 * Seite, auf der der Regler gerade steht (siehe TEXT_FLIP in mix.ts).
 */
const MIXED = [
  // Nebentext: wird unten in applyTheme noch an Schrift und Grund angepasst.
  '--c-ink-soft',
  '--c-ink-faint',
  '--c-selected',
  '--c-elevated',
  '--c-bg',
  '--c-surface',
  '--c-surface-2',
  '--c-accent-weak',
  '--c-border',
  '--c-rule',
  '--autoscroll-glass',
  '--c-scrim',
];

/** Werte der beiden Theme-Dateien, einmal gelesen. */
let palettes: { light: Record<string, string>; dark: Record<string, string> } | null = null;

function readPalettes(): NonNullable<typeof palettes> {
  if (palettes) return palettes;
  const root = document.documentElement;
  const before = root.dataset.theme;
  const read = (id: string): Record<string, string> => {
    root.dataset.theme = id;
    const style = getComputedStyle(root);
    return Object.fromEntries([...MIXED, '--c-ink'].map((name) => [name, style.getPropertyValue(name).trim()]));
  };
  palettes = { light: read('paper'), dark: read('dark') };
  if (before) root.dataset.theme = before;
  return palettes;
}

/**
 * Das zuletzt gewuenschte Design, also auch "auto".
 * Die Oberflaeche braucht diesen Wert, um den richtigen Schalter zu markieren.
 */
let requested: string = DEFAULT_THEME_ID;

export function currentThemeId(): string {
  return requested;
}

/** Setzt das Theme am Dokument und passt die Statusleistenfarbe an. */
export function applyTheme(id: string): ThemeDef {
  requested = id;
  const theme = resolveTheme(id);
  const tone = themeTone(id);
  const mix = shapeTone(tone);
  const style = document.documentElement.style;
  for (const name of MIXED) style.removeProperty(name);
  clearAppearance();
  // Zwischenstufe: die Flaechen beider Seiten mischen.
  if (mix > 0 && mix < 1) {
    const { light, dark } = readPalettes();
    for (const name of MIXED) {
      const a = light[name];
      const b = dark[name];
      if (a && b) style.setProperty(name, mixColor(a, b, mix));
    }
  }
  document.documentElement.dataset.theme = theme.id;
  // Hintergrund und Farben (theme/appearance.ts) in der passenden Fassung.
  const bgColor = paintAppearance(mix, theme.colorScheme);
  // Auf grauem Grund waeren die festen Nebentext-Farben zu blass: dort
  // richten sie sich nach Schrift und Grund.
  if (mix > 0 && mix < 1) {
    const computed = getComputedStyle(document.documentElement);
    const ink = computed.getPropertyValue('--c-ink').trim();
    const bg = computed.getPropertyValue('--c-bg').trim();
    style.setProperty('--c-ink-soft', mixColor(ink, bg, 0.3));
    style.setProperty('--c-ink-faint', mixColor(ink, bg, 0.46));
  }
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = bgColor ?? (mix > 0 && mix < 1 ? mixColor(findTheme('paper').themeColor, findTheme('dark').themeColor, mix) : theme.themeColor);
  return theme;
}

/**
 * Haelt das Theme im Modus "auto" mit dem Geraet im Gleichtakt.
 * Liefert die Abmeldefunktion.
 */
export function watchSystemTheme(getThemeId: () => string): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    if (getThemeId() === AUTO_THEME_ID) applyTheme(AUTO_THEME_ID);
  };
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
